"use client";

import { useFloatingContext } from "@faire/slate/private/floating/FloatingContext";
import { FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import React, { useEffect, useRef } from "react";

import { useModalContext } from "./ModalContext";
import { useModalInitialFocus } from "./useModalInitialFocus";

const ModalPortalFocusManager: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { open, floatingRootContext, refs } = useFloatingContext();
  const initialFocusRef = useRef<HTMLElement | null>(null);
  const {
    getInitialFocusElement,
    runInitialFocusWhenOpen,
  } = useModalInitialFocus({
    floatingElement: refs.floating,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    const element = getInitialFocusElement();
    if (element) {
      initialFocusRef.current = element;
    }
    return runInitialFocusWhenOpen();
  }, [
    getInitialFocusElement,
    open,
    runInitialFocusWhenOpen,
  ]);

  return (
    <FloatingFocusManager
      context={floatingRootContext}
      initialFocus={initialFocusRef}
    >
      <>{children}</>
    </FloatingFocusManager>
  );
};

export const ModalPortal: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { open } = useFloatingContext();
  const { animating } = useModalContext();

  if (!open && !animating) {
    return null;
  }

  return (
    <FloatingPortal>
      <ModalPortalFocusManager>{children}</ModalPortalFocusManager>
    </FloatingPortal>
  );
};
